import React from 'react';
import '../../../styles/components/clients/pinned-list/PinnedListActions.css';

const PinnedListActions = ({
  pinnedClients,
  pinnedSearchQuery,
  setPinnedSearchQuery,
  sortedClients,
  calculateFilteredRevenue,
  activeTierFilters,
  setActiveTierFilters,
  viewType,
  toggleViewType,
  useUniformColor,
  setUseUniformColor,
  useTierColors,
  setUseTierColors,
  usePieChartMarkers,
  setUsePieChartMarkers,
  isHardPinned,
  toggleHardPin,
  openExportForm,
  exportToExcel,
  unpinAllClients,
  isAdmin = true,
  isClientLocked,
  unpinClient,
  setPinnedClients,
  setOrderedPinnedClients
}) => {
  
  // Handle Clear All button click - remove non-locked clients, keep locked ones
  const handleClearAllClick = () => {
    // Find all locked clients from the pinned clients list
    const lockedClients = pinnedClients.filter(client => isClientLocked(client));
    
    if (lockedClients.length === 0) {
      // If no locked clients, remove all clients (original behavior)
      unpinAllClients();
    } else {
      // Direct state manipulation: set pinned clients to only locked clients
      // This bypasses the individual unpinClient calls that cause re-render issues
      if (setPinnedClients && setOrderedPinnedClients) {
        setPinnedClients(lockedClients);
        setOrderedPinnedClients(lockedClients);
      } else {
        // Fallback: use the original unpinAllClients
        unpinAllClients();
      }
    }
  };
  return (
    <div className="pinned-actions">
      {pinnedClients.length > 0 && (
        <>
          <div className="search-section">
            <div className="search-with-totals">
              <input
                type="text"
                placeholder="Search all client data..."
                value={pinnedSearchQuery}
                onChange={(e) => setPinnedSearchQuery(e.target.value)}
                className="pinned-search-input"
              />
              <div className="search-totals">
                {sortedClients.length} clients | {calculateFilteredRevenue(sortedClients).yearSpecific}
              </div>
            </div>
          </div>
          
          <button 
            className={`view-toggle ${viewType === 'table' ? 'active' : ''}`} 
            onClick={toggleViewType}
            title="Toggle view type"
          >
            {viewType === 'table' ? 'Switch to Cards' : 'Switch to Table'}
          </button>
          
          <button 
            className={`tier-colors-toggle ${usePieChartMarkers ? 'active' : ''}`}
            onClick={() => setUsePieChartMarkers(!usePieChartMarkers)}
            title={usePieChartMarkers ? "Switch to classic circles" : "Switch to pie chart markers"}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.21 15.89A10 10 0 1 1 8 2.83"></path>
              <path d="M22 12A10 10 0 0 0 12 2v10z"></path>
            </svg>
            <span>Tiers</span>
          </button>
          
          <button 
            className={`color-toggle ${useUniformColor ? 'active' : ''}`}
            onClick={() => setUseUniformColor(!useUniformColor)}
            title={useUniformColor ? "Use original colors" : "Use uniform color"}
            style={{ display: 'none' }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <circle cx="12" cy="12" r="4"></circle>
            </svg>
            <span>Uniform Color</span>
          </button>
          
          <button 
            className={`sticky-mode-btn ${isHardPinned ? 'active' : ''} ${!isAdmin ? 'disabled' : ''}`}
            onClick={isAdmin ? toggleHardPin : undefined}
            disabled={!isAdmin}
            title={!isAdmin ? "Sticky Mode is only available to admin users" : (isHardPinned ? "Disable Sticky Mode" : "Enable Sticky Mode")}
            style={{
              cursor: !isAdmin ? 'not-allowed' : 'pointer',
              opacity: !isAdmin ? 0.5 : 1
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={!isAdmin ? '#ccc' : 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
              <polyline points="10 17 15 12 10 7"></polyline>
              <line x1="15" y1="12" x2="3" y2="12"></line>
            </svg>
            <span style={{ color: !isAdmin ? '#ccc' : 'inherit' }}>Sticky Mode {isHardPinned ? "On" : "Off"}</span>
          </button>
          
          <button 
            className="export-btn" 
            onClick={() => openExportForm('email')}
            title="Send to email"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
              <polyline points="22,6 12,13 2,6"></polyline>
            </svg>
            <span>Email</span>
          </button>
          
          <button 
            className="export-btn" 
            onClick={exportToExcel}
            title="Export to Excel"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="12" y1="18" x2="12" y2="12"></line>
              <line x1="9" y1="15" x2="15" y2="15"></line>
            </svg>
            <span>Excel</span>
          </button>
          
          <button 
            className="clear-btn" 
            onClick={handleClearAllClick}
            title="Remove all non-locked clients"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
            <span>Clear All</span>
          </button>
        </>
      )}
    </div>
  );
};

export default PinnedListActions;
